/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { ReactComponent as Line } from "../../../assets/images/line.svg";
import { ReactComponent as MapPoint } from "../../../assets/images/Mappoint.svg";

function classNames(...xs) {
    return xs.filter(Boolean).join(" ");
}

export default function PublicHeader({ disabled = false }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // ✅ header โปร่งใสตอนบนสุด (ยกเว้น disabled=true)
    useEffect(() => {
        if (disabled) return; // ✅ ถ้า disabled ไม่ต้องฟัง scroll
        const THRESHOLD = 8;
        const onScroll = () => setScrolled(window.scrollY > THRESHOLD);

        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, [disabled]);

    // ✅ กันเลื่อนหน้าตอน drawer เปิด
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => (document.body.style.overflow = "");
    }, [open]);

    // ✅ ปิด drawer ถ้า resize ไป desktop
    useEffect(() => {
        const onResize = () => {
            if (window.innerWidth >= 768) setOpen(false);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const NavItem = useMemo(
        () =>
            function NavItem({ to, label, end }) {
                return (
                    <NavLink to={to} end={end} onClick={() => setOpen(false)} className="block">
                        {({ isActive }) => (
                            <span
                                className={classNames(
                                    "relative inline-flex px-3 py-2 text-sm font-medium transition",
                                    isActive ? "text-white" : "text-white/80 hover:text-white"
                                )}
                            >
                                {label}
                                {isActive && (
                                    <span className="absolute left-3 right-3 -bottom-1 h-[3px] rounded-full bg-white" />
                                )}
                            </span>
                        )}
                    </NavLink>
                );
            },
        []
    );

    // ✅ ถ้า disabled=true ให้มีพื้นหลังตลอด
    const showBg = disabled || scrolled;

    return (
        <>
            <header
                className={classNames(
                    "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
                    showBg ? "bg-[#1B3147B2] backdrop-blur-xl" : "bg-transparent"
                )}
            >
                <div className="mx-auto max-w-6xl px-4 sm:px-6 py-2 flex items-center justify-between">
                    <div className="text-white font-medium tracking-wide">Bestphone</div>

                    <div className="hidden md:flex items-center gap-3">
                        <NavItem to="/" label="หน้าแรก" end />
                        <NavItem to="/products" label="สินค้าทั้งหมด" />

                        <a
                            href="https://lin.ee/VUW5WFX"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 inline-flex items-center gap-2 px-4 py-2 md:rounded-xl md:bg-white/15 text-white text-sm font-light hover:bg-white/20 transition"
                        >
                            <Line className="w-5 h-5 text-white" />
                            LineOA
                        </a>

                        <span className="mx-1 h-7 bg-white/30 w-[2px]" />

                        <a href="https://maps.app.goo.gl/vpwoTXa7R4ZMtjvU8" title="พิกัดแผนที่ร้าน" className="inline-flex items-center justify-center w-10 h-10 transition">
                            <MapPoint className="w-5 h-5" />
                        </a>
                    </div>

                    <div className="flex md:hidden items-center gap-2">
                        <a
                            href="#"
                            aria-label="LineOA"
                            className="inline-flex items-center justify-center h-10 w-10 md:rounded-xl md:bg-white/15 hover:bg-white/20 transition"
                        >
                            <Line className="w-5 h-5 text-white" />
                        </a>

                        <a
                            href="#"
                            aria-label="Map"
                            className="inline-flex items-center justify-center h-10 w-10 hover:bg-white/20 transition"
                        >
                            <MapPoint className="w-5 h-5" />
                        </a>

                        <button
                            type="button"
                            onClick={() => setOpen(true)}
                            className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white/15 hover:bg-white/20 transition"
                            aria-label="Open menu"
                        >
                            <Menu className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>
            </header>

            {open && (
                <>
                    <div className="fixed inset-0 z-[60] bg-black/45 md:hidden" onClick={() => setOpen(false)} />

                    <div className="fixed top-0 right-0 z-[61] h-full w-[280px] md:hidden bg-[#0B1E3A]/95 backdrop-blur border-l border-white/10">
                        <div className="px-4 py-4 flex items-center justify-between border-b border-white/10">
                            <div className="text-white font-semibold">Bestphone</div>
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/15 transition"
                                aria-label="Close menu"
                            >
                                <X className="w-5 h-5 text-white" />
                            </button>
                        </div>

                        <div className="px-2 py-3 space-y-1">
                            <NavItem to="/" label="หน้าแรก" end />
                            <NavItem to="/products" label="สินค้าทั้งหมด" />
                        </div>

                        <div className="px-4 pt-3">
                            <a
                                href="#"
                                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/15 text-white text-sm font-medium hover:bg-white/20 transition"
                            >
                                <Line className="w-5 h-5 text-white" />
                                LineOA
                            </a>

                            <a
                                href="#"
                                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/15 text-white text-sm font-medium hover:bg-white/20 transition"
                            >
                                <MapPoint className="w-5 h-5" />
                                พิกัดร้าน
                            </a>
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
